import { useMemo, useState, type KeyboardEvent, type MouseEvent } from "react";

import type { CanalePanoramica } from "@/lib/api";
import { METRICHE, giornoBreve, nf, pf, scala, serieAndamento, type Metrica } from "@/lib/panoramica";
import { cn } from "@/lib/utils";

const LARGO = 1280;
const ALTO = 224;
const X0 = 48;
const X1 = 1160;
const Y0 = 16;
const Y1 = 186;

interface GraficoAndamentoProps {
  canali: CanalePanoramica[];
  metrica: Metrica;
  onMetrica: (m: Metrica) => void;
  selezionato: string | null;
  etichettaTotale: string;
}

function tracciato(valori: Array<number | null>, x: (i: number) => number, y: (v: number) => number): string {
  let d = "";
  let aperto = false;
  valori.forEach((v, i) => {
    if (v === null) {
      aperto = false;
      return;
    }
    d += `${aperto ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)} `;
    aperto = true;
  });
  return d.trim();
}

function ultimo(valori: Array<number | null>): { i: number; v: number } | null {
  for (let i = valori.length - 1; i >= 0; i -= 1) {
    const v = valori[i];
    if (v !== null) return { i, v };
  }
  return null;
}

function primo(valori: Array<number | null>): { i: number; v: number } | null {
  const i = valori.findIndex((v) => v !== null);
  return i === -1 ? null : { i, v: valori[i] as number };
}

export function GraficoAndamento({
  canali,
  metrica,
  onMetrica,
  selezionato,
  etichettaTotale,
}: GraficoAndamentoProps): JSX.Element {
  const [giorno, setGiorno] = useState<number | null>(null);
  const serie = useMemo(() => serieAndamento(canali, metrica, selezionato), [canali, metrica, selezionato]);
  const etichettaMetrica = METRICHE.find((m) => m.id === metrica)?.label ?? "";
  const nomeSelezionato = selezionato ? canali.find((c) => c.marketplace_id === selezionato)?.label ?? null : null;
  const formato = (v: number) => (metrica === "vinte_pct" ? pf(v) : nf(Math.round(v)));
  const n = serie.giorni.length;

  const chip = (
    <div className="flex flex-wrap items-center gap-1.5">
      {METRICHE.map((m) => (
        <button
          key={m.id}
          type="button"
          aria-pressed={m.id === metrica}
          onClick={() => {
            setGiorno(null);
            onMetrica(m.id);
          }}
          className={cn("btn btn-sm", m.id === metrica && "toggle-on")}
        >
          {m.label}
        </button>
      ))}
    </div>
  );

  if (n < 2) {
    return (
      <div className="card-stack px-5 pb-[18px] pt-4">
        {chip}
        <p className="mt-4 text-sm text-ink-2">
          {n === 0
            ? "Ancora nessun giorno salvato: il primo ricalcolo arriva entro un'ora."
            : `L'andamento si riempie un giorno alla volta: primo giorno ${giornoBreve(serie.giorni[0])}.`}
        </p>
      </div>
    );
  }

  const valori = [...serie.totale, ...(serie.canale ?? [])].filter((v): v is number => v !== null);
  const { lo, hi, passo } = scala(valori);
  const x = (i: number) => X0 + (i * (X1 - X0)) / (n - 1);
  const y = (v: number) => Y1 - ((v - lo) / (hi - lo)) * (Y1 - Y0);
  const tacche: number[] = [];
  for (let v = lo; v <= hi + 1e-9; v += passo) tacche.push(v);
  const etichetteX = [...new Set([0, 5, 10, 15, 20, 25, n - 1].filter((i) => i < n))];
  const fineTot = ultimo(serie.totale);
  const fineSel = serie.canale ? ultimo(serie.canale) : null;
  let yTot = fineTot ? y(fineTot.v) + 4 : 0;
  let ySel = fineSel ? y(fineSel.v) + 4 : 0;
  if (fineTot && fineSel && Math.abs(yTot - ySel) < 13) {
    if (yTot <= ySel) {
      yTot -= 7;
      ySel += 7;
    } else {
      yTot += 7;
      ySel -= 7;
    }
  }
  const inizioTot = primo(serie.totale);
  const equivalente =
    `${etichettaMetrica}, ${etichettaTotale}: oggi ${fineTot ? formato(fineTot.v) : "—"}, ` +
    `il ${giornoBreve(serie.giorni[inizioTot?.i ?? 0])} ${inizioTot ? formato(inizioTot.v) : "—"}.` +
    (nomeSelezionato
      ? serie.canale
        ? ` ${nomeSelezionato}: oggi ${fineSel ? formato(fineSel.v) : "—"}.`
        : ` ${nomeSelezionato} non ha questo indicatore: nessuna seconda linea.`
      : "");

  const g = giorno === null || n === 0 ? null : Math.min(giorno, n - 1);

  const muovi = (e: MouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width) return;
    const px = ((e.clientX - r.left) * LARGO) / r.width;
    const i = Math.round(((px - X0) * (n - 1)) / (X1 - X0));
    setGiorno(Math.max(0, Math.min(n - 1, i)));
  };

  const sposta = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const base = g ?? n - 1;
    const delta = e.key === "ArrowLeft" ? -1 : 1;
    setGiorno(Math.max(0, Math.min(n - 1, base + delta)));
  };

  const valoreTot = g === null ? null : serie.totale[g];
  const valoreSel = g === null || !serie.canale ? null : serie.canale[g];

  return (
    <div className="card-stack px-5 pb-[18px] pt-4">
      <div className="mb-3.5 flex items-center gap-1.5">
        {chip}
        <div className="ml-auto flex items-center gap-4">
          <span className="flex items-center gap-[7px] text-label text-ink-2">
            <span className="block h-0.5 w-4 bg-ink" aria-hidden="true" />
            {etichettaTotale}
          </span>
          {serie.canale && nomeSelezionato ? (
            <span className="flex items-center gap-[7px] text-label font-medium text-ink">
              <span className="block h-0.5 w-4 bg-info" aria-hidden="true" />
              {nomeSelezionato}
            </span>
          ) : null}
        </div>
      </div>
      <div className="relative">
        <svg
          viewBox={`0 0 ${LARGO} ${ALTO}`}
          className="block h-auto w-full focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          role="img"
          aria-label={equivalente}
          tabIndex={0}
          onMouseMove={muovi}
          onMouseLeave={(e) => {
            if (document.activeElement !== e.currentTarget) setGiorno(null);
          }}
          onFocus={() => {
            if (n > 0 && giorno === null) setGiorno(n - 1);
          }}
          onBlur={() => setGiorno(null)}
          onKeyDown={sposta}
        >
          {tacche.map((v) => (
            <g key={v}>
              <line x1={X0} x2={X1} y1={y(v)} y2={y(v)} stroke="hsl(var(--line))" strokeWidth={1} />
              <text x={40} y={y(v) + 3} textAnchor="end" className="fill-ink-3 font-mono text-micro tabular-nums">
                {formato(v)}
              </text>
            </g>
          ))}
          {etichetteX.map((i) => (
            <text key={i} x={x(i)} y={208} textAnchor="middle" className="fill-ink-3 font-mono text-micro">
              {giornoBreve(serie.giorni[i])}
            </text>
          ))}
          {g !== null ? <line x1={x(g)} x2={x(g)} y1={14} y2={Y1} stroke="hsl(var(--ink-3))" strokeWidth={1} /> : null}
          <path d={tracciato(serie.totale, x, y)} fill="none" stroke="hsl(var(--ink))" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          {serie.canale ? (
            <path d={tracciato(serie.canale, x, y)} fill="none" stroke="hsl(var(--info))" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          ) : null}
          {g !== null && valoreTot !== null ? <circle cx={x(g)} cy={y(valoreTot)} r={3.5} fill="hsl(var(--ink))" /> : null}
          {g !== null && valoreSel !== null && valoreSel !== undefined ? (
            <circle cx={x(g)} cy={y(valoreSel)} r={3.5} fill="hsl(var(--info))" />
          ) : null}
          {fineTot ? (
            <text x={1168} y={yTot} className="fill-ink font-mono text-label font-semibold tabular-nums">
              {formato(fineTot.v)}
            </text>
          ) : null}
          {fineSel ? (
            <text x={1168} y={ySel} className="fill-info font-mono text-label font-semibold tabular-nums">
              {formato(fineSel.v)}
            </text>
          ) : null}
        </svg>
        {g !== null ? (
          <div
            className="pointer-events-none absolute top-2 min-w-[132px] rounded-md border border-line-2 bg-card px-2.5 py-2 shadow-popover"
            style={{ left: `${(Math.min(x(g) + 14, LARGO - 160) / LARGO) * 100}%` }}
          >
            <div className="mb-[5px] text-micro font-semibold text-ink-3">
              {giornoBreve(serie.giorni[g])}
            </div>
            <div className="font-mono text-md font-semibold tabular-nums leading-tight text-ink">
              {valoreTot === null ? "—" : formato(valoreTot)}
            </div>
            <div className="mt-px text-label text-ink-3">{etichettaTotale}</div>
            {serie.canale && nomeSelezionato ? (
              <div className="mt-[7px] border-t border-line pt-[7px]">
                <div className="font-mono text-md font-semibold tabular-nums leading-tight text-info">
                  {valoreSel === null || valoreSel === undefined ? "—" : formato(valoreSel)}
                </div>
                <div className="mt-px text-label text-ink-3">{nomeSelezionato}</div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <p className="mt-3 border-t border-line pt-3 text-xs leading-relaxed text-ink-2">{equivalente}</p>
    </div>
  );
}
